import { skipToken } from "@reduxjs/toolkit/query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useGetConversationsQuery } from "src/features/ai-chat/api/aiChatApi.js";
import { useAiChatAccess } from "src/features/ai-chat/hooks/useAiChatAccess.js";
import { useOnboardingStatus } from "src/features/ai-chat/hooks/useOnboardingStatus.js";
import { useLoadedCompanyDomain } from "src/routes/routeDataHooks.js";
import {
	AiChatWidgetView,
	type AiChatWidgetState,
} from "./AiChatWidgetView.js";

const RECENT_CONVERSATIONS_SHOWN = 4;

export const AiChatWidget = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const companyDomain = useLoadedCompanyDomain();
	const { hasAccess, access, optedIn, isLoading } = useAiChatAccess(
		companyDomain.id,
	);
	// Company opt-in and this user's own onboarding consent are separate gates — see AiChatPage.
	const { needsOnboarding, isLoading: isOnboardingLoading } =
		useOnboardingStatus(companyDomain.id);
	// Absolute: Exopen AI lives at domain level, with no route under a company.
	const aiChatBase = `/company-domains/${companyDomain.id}/ai-chat`;
	const { data: conversations, isError: recentConversationsError } =
		useGetConversationsQuery(
			hasAccess && optedIn && !needsOnboarding ? companyDomain.id : skipToken,
		);

	const state: AiChatWidgetState =
		isLoading || (optedIn && isOnboardingLoading)
			? "loading"
			: !hasAccess
				? "noAccess"
				: !optedIn
					? "notOptedIn"
					: needsOnboarding
						? "needsOnboarding"
						: "ready";

	return (
		<AiChatWidgetView
			state={state}
			suggestions={[
				// WHY scoped to "the group": an unscoped prompt makes the assistant stop and ask which entities to include
				t("Show forecast vs budget for the whole group"),
				t("Show budget deviations for the group in Q1"),
				t("Summarize last month's results for the CFO"),
			]}
			recentConversations={(conversations ?? [])
				.slice(0, RECENT_CONVERSATIONS_SHOWN)
				.map(({ id, title }) => ({ id, title }))}
			recentConversationsError={recentConversationsError}
			onSubmitPrompt={(prompt) => {
				navigate(`${aiChatBase}/conversations/new`, {
					state: { initialPrompt: prompt },
				});
			}}
			onOpenConversation={(conversationId) => {
				navigate(`${aiChatBase}/conversations/${conversationId}`);
			}}
			noAccessReason={access.allowed ? undefined : access.reason}
			onOptIn={() => {
				navigate(aiChatBase);
			}}
		/>
	);
};
